/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of selected options
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// product tabs accordion functionality

document.addEventListener("DOMContentLoaded", () => {
  const MAX_VISIBLE_ROWS = 10;

  const tabs = document.querySelectorAll(".tab");
  const tabContents = document.querySelectorAll(".tab-content");
  const showMoreBtn = document.querySelector(".show-more-btn");
  if (!showMoreBtn) return;

  let currentTabContent = null;
  let currentHiddenContainer = null;

  const setupTabContent = (tabContent) => {
    const specRows = Array.from(tabContent.querySelectorAll(".spec-row"));
    if (specRows.length === 0) {
      if (!tabContent.querySelector(".no-data")) {
        const noData = document.createElement("div");
        noData.classList.add("no-data");
        noData.textContent = "No Data Available";
        tabContent.appendChild(noData);
      }
      showMoreBtn.style.display = "none";
      return;
    } else {
      const existingNoData = tabContent.querySelector(".no-data");
      if (existingNoData) existingNoData.remove();
    }

    const visibleRows = specRows.slice(0, MAX_VISIBLE_ROWS);
    const hiddenRows = specRows.slice(MAX_VISIBLE_ROWS);

    // Remove previous hidden container if it exists
    const existingHidden = tabContent.querySelector(".hidden-item");
    if (existingHidden) existingHidden.remove();

    if (hiddenRows.length === 0) {
      showMoreBtn.style.display = "none";
      return;
    }

    const hiddenContainer = document.createElement("div");
    hiddenContainer.classList.add("hidden-item");
    hiddenContainer.style.display = "none";

    hiddenRows.forEach((row) => hiddenContainer.appendChild(row));
    tabContent.appendChild(hiddenContainer);

    showMoreBtn.style.display = "block";
    showMoreBtn.textContent = "Show More";

    currentTabContent = tabContent;
    currentHiddenContainer = hiddenContainer;
  };

  showMoreBtn.addEventListener("click", () => {
    if (!currentHiddenContainer) return;
    const isHidden = currentHiddenContainer.style.display === "none";
    currentHiddenContainer.style.display = isHidden ? "block" : "none";
    showMoreBtn.textContent = isHidden ? "Show Less" : "Show More";
  });

  // Initialize tabs and content
  tabContents.forEach((tabContent, idx) => {
    const isActive = tabContent.classList.contains("active");
    tabContent.style.display = isActive ? "block" : "none";
    if (isActive) setupTabContent(tabContent);
  });

  // Tab switching
  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      const targetId = tab.getAttribute("data-tab");

      tabs.forEach((t) => t.classList.remove("active"));
      tab.classList.add("active");

      tabContents.forEach((tabContent) => {
        const isTarget = tabContent.id === targetId;
        tabContent.style.display = isTarget ? "block" : "none";
        tabContent.classList.toggle("active", isTarget);

        if (isTarget) {
          setupTabContent(tabContent);
        }
      });
      // Reset button state
      showMoreBtn.textContent = "Show More";
    });
  });
});

function initStarRating() {
  if (document.querySelectorAll(".starFill").length > 0) {
    document.querySelectorAll(".starFill").forEach((el) => {
      const rating = parseFloat(el.dataset.rating);
      const percentage = (rating / 5) * 100;
      el.style.width = `${percentage}%`;
    });
  }
}

let scrollY = 0;

function disableBodyScroll() {
  scrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
}

function enableBodyScroll() {
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';
  window.scrollTo(0, scrollY);
}

// Google map popup functionality for collection
function initMapPopup() {
  const productCards = document.querySelectorAll('.card--product');
  productCards.forEach(card => {
    const mapBtn = card.querySelector('.open-map-btn');
    const popupOverlay = card.querySelector(".map-popup-overlay"); 
    const closeBtn = card.querySelector(".close-map-btn");
    const stickyEl = document.getElementById('gf-controls-container');
    const compareDrawerBtn = document.querySelector('.btn--compare');
    
    if (!mapBtn || !popupOverlay || !closeBtn) return;

    const iFrame = popupOverlay.querySelector('iframe');
    const originalSrc = iFrame.dataset.src;

    mapBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (!iFrame.src) {
        iFrame.setAttribute('src', originalSrc);
      }
      popupOverlay.classList.add("active");
      document.body.classList.add('no-scroll');
      stickyEl.style.position = 'static';
      if (compareDrawerBtn) compareDrawerBtn.style.display = 'none';
      disableBodyScroll();
    });

    closeBtn.addEventListener('click', () => {
      popupOverlay.classList.remove("active");
      document.body.classList.remove('no-scroll');
      stickyEl.style.position = 'sticky';
      if (compareDrawerBtn) compareDrawerBtn.style.display = 'block';
      iFrame.removeAttribute('src');
      enableBodyScroll();
    });

    popupOverlay.addEventListener("click", (e) => {
      if (e.target === popupOverlay) {
        popupOverlay.classList.remove("active");
        document.body.classList.remove('no-scroll');
        stickyEl.style.position = 'sticky';
        if (compareDrawerBtn) compareDrawerBtn.style.display = 'block';
        iFrame.removeAttribute('src');
        enableBodyScroll();
      }
    });
  });
}
// Google map popup functionality ends

// Google map popup functionality for PDP
const productImage = document.querySelector('.product-image');
function openMapPopupPDP() {
  document.querySelectorAll('.open-map-btn').forEach((btn) => {
    const popupOverlay = btn.nextElementSibling;
    const closeBtn = popupOverlay.querySelector('.close-map-btn');
    const iFrame = popupOverlay.querySelector('iframe');
    const originalSrc = iFrame.getAttribute('src');

    btn.addEventListener('click', () => {
      popupOverlay.classList.add('active');
      document.body.style.overflow = 'hidden';
      disableBodyScroll();
    });

    closeBtn.addEventListener('click', () => {
      popupOverlay.classList.remove('active');
      document.body.style.overflow = '';
      iFrame.setAttribute('src', originalSrc);
      enableBodyScroll();
    });

    // Click outside to close
    popupOverlay.addEventListener('click', (e) => {
      if (e.target === popupOverlay) {
        popupOverlay.classList.remove('active');
        document.body.style.overflow = '';
        iFrame.setAttribute('src', originalSrc);
        enableBodyScroll();
      }
    });
  });
}

if (productImage) {
  if (productImage.complete) {
    console.log('Image loaded, safe to initialize popup');
    openMapPopupPDP();
  } else {
    productImage.addEventListener('load', () => {
      console.log('Image loaded (after load event), initializing popup');
      openMapPopupPDP();
    });
  }
}
// Google map popup functionality ends


// Wishlist functionality starts
const API = 'https://api.vbay.eqserver.net/api';
function toggleWishlist() {
  const wishlistButtons = document.querySelectorAll('.wishlist-toggle');
  wishlistButtons.forEach(btn => {

    if (btn.dataset.wishlistBound === "true") return;
    btn.dataset.wishlistBound = "true";

    btn.addEventListener('click', async function(e) {
      e.preventDefault();
      const isAdded = this.classList.contains('wishlist-added');
      const vehicleId = this.dataset.vehicleId;
      const customer_email = window.ShopCustomer?.email || '';
      const customer_id = window.ShopCustomer?.id || '';
      const token = window.globalToken;

      if (!customer_email || !customer_id) {
        console.warn('Wishlist: missing ShopCustomer');
        return;
      }
      
      const payload = {
        customer_email: customer_email,
        customer_id: customer_id,
        vehicle_id: vehicleId
      }
      
      const endpoint = isAdded ? `${API}/wishListShopify/removeFormWishList` : `${API}/wishListShopify/createWishList`;
      const method = isAdded ? 'DELETE' : 'POST';
      
      try {
        const response = await fetch(endpoint, {
          method: method,
          headers: {
            'Authorization': `${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        
        if(!response.ok) throw new Error(`Wishlist ${isAdded ? 'removal' : 'addition'} failed`);
        
        const result = await response.json();
        console.log('Wishlist API result:', result);
        
        this.classList.toggle('wishlist-added');
        this.classList.toggle('wishlist-btn');
        
        alert(`Wishlist ${isAdded ? 'removed' : 'added'} successfully!`);
      } catch (error) {
        console.error('Wishlist toggle error:', error);
        alert('Wishlist action failed. Please try again.');
      }
    })
  })
}

async function fetchWishList() {
  const customer_email = window.ShopCustomer?.email || '';

  if (!customer_email) {
    console.warn('Wishlist fetch: missing ShopCustomer');
    return;
  }
  const wishlistButtons = document.querySelectorAll('.wishlist-toggle')
  try {
    const response = await fetch(`${API}/wishListShopify/fetchShopifyWishList?customer_email=${customer_email}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) throw new Error('Failed to fetch wishlist');
    
    const data = await response.json();
    const wishlistedItems = data.payload.rows || [];
    
    wishlistButtons.forEach(btn => {
      const productId = Number(btn.getAttribute('data-product-id'));
      const isWishlisted = wishlistedItems.some(item => item.Vehicle?.shopifyProductId === productId)
      
      if (isWishlisted) {
        btn.classList.remove('wishlist-btn');
        btn.classList.add('wishlist-added');
      }
    })
  } catch(error) {
    console.error('Error fetching wishlist:', error);
  }
}
// Wishlist functionality ends

// Booking functionality starts
function createBooking() {
  document.querySelectorAll('.calendar-container').forEach(container => {
    const bookingBtn = container.querySelector('.calendar__book-btn');
    const dateInput = container.querySelector('.booking-input');
    const calendarWidget = container.querySelector('.calendar-widget');
    const calendarOverlay = container.querySelector('.calendar-overlay');

    if (bookingBtn) {
      bookingBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const dateRange = dateInput?.value?.split(' — ');
        const startDate = dateRange?.[0]?.trim();
        const endDate = dateRange?.[1]?.trim();
        const token = window.globalToken;
        const customer_email = window.ShopCustomer?.email || '';
        const customer_id = window.ShopCustomer?.id || '';
        const vehicle_id = bookingBtn.dataset.vehicleId;

        if (!customer_email || !customer_id) {
          showToast("Please log in first.", "white");
          return;
        }

        if (!startDate || !endDate) {
          alert("Please select a valid date range.");
          return;
        }

        const bookingDateValue = new Date(startDate).toISOString();
        const endBookingDateValue = new Date(endDate).toISOString();

        try {
          const response = await fetch(`${api_url}/rent/booking`, {
            method: 'POST',
            headers: {
              'Authorization': `${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              start_date: bookingDateValue,
              end_date: endBookingDateValue,
              customer_email: customer_email,
              customer_id: customer_id,
              vehicle_id: vehicle_id
            })
          });

          const data = await response.json();

          if (response.ok && data.status === 201) {
            showToast(data.message || "Booking successful!", "white");
            calendarWidget.classList.remove('show');
            dateInput.value = '';
            calendarOverlay.style.display = 'none';
          } else {
            showToast(data.message || "Booking failed!", "white");
          }

        } catch (error) {
          console.error('Booking failed:', error);
          showToast("Booking failed!", "white");
        }
      });
    }
  });
}

function showToast(message, color = 'white', duration = 2000) {
  let toast = document.querySelector(".success-message");
  if (toast) toast.remove();

  toast = document.createElement("span");
  toast.classList.add("success-message");
  toast.textContent = message;
  toast.style.color = color;
  document.body.appendChild(toast);

  setTimeout(() => toast.classList.add('show'), 10);

  setTimeout(() => {
    toast.classList.remove('show');
    toast.addEventListener('transitionend', () => toast.remove());
  }, duration);
}

function multiPassUrl() {
  const isShopifyUser = true;
  const actionBtns = document.querySelectorAll('.redirect-wishlist-btn, .booking_redirect_btn');

  actionBtns.forEach(icon => {
    icon.addEventListener('click', (event) => {
      event.preventDefault();
      const path = window.location.pathname;
      const fullUrl = window.location.origin + path;

      const redirectUrl = `https://vbay.eqserver.net/login?url=${encodeURIComponent(fullUrl)}&isShopifyUser=${isShopifyUser}`;
      window.location.href = redirectUrl;
    })
  })
}

document.addEventListener('DOMContentLoaded', () => {
  initStarRating();
  initMapPopup();
  toggleWishlist();
  fetchWishList();
  createBooking();
  multiPassUrl();
});


